import React, { useState } from 'react';
import { Box, Typography, Stack, keyframes, Chip, Button, CircularProgress } from '@mui/material';
import { useTheme, alpha, Theme } from '@mui/material/styles';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import VisibilityBadge from './UserViewBadge';
import AiChatWidgetCard from '../AiChatWidgetCard';
import { aiAnalysisPdfApi } from '../../../api/aiAnalysisPdfApi';

const MONO = 'SF Mono, Menlo, Consolas, monospace';

const pulse = keyframes`
  0%, 80%, 100% { opacity: 0.3; transform: scale(0.85); }
  40% { opacity: 1; transform: scale(1); }
`;

const cursorBlink = keyframes`
  0%, 50% { opacity: 1; }
  51%, 100% { opacity: 0.2; }
`;

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(6px); }
  to   { opacity: 1; transform: translateY(0); }
`;

// Safe inline markdown renderer — returns React nodes, no innerHTML.
function renderInline(text: string): React.ReactNode[] {
  const pattern = /(\*\*[^*]+\*\*|\*[^*]+\*|`[^`]+`)/g;
  const parts: React.ReactNode[] = [];
  let lastIdx = 0;
  let key = 0;
  for (const m of text.matchAll(pattern)) {
    const start = m.index ?? 0;
    if (start > lastIdx) parts.push(text.slice(lastIdx, start));
    const token = m[0];
    if (token.startsWith('**')) {
      parts.push(<strong key={key++}>{token.slice(2, -2)}</strong>);
    } else if (token.startsWith('*')) {
      parts.push(<em key={key++}>{token.slice(1, -1)}</em>);
    } else if (token.startsWith('`')) {
      parts.push(<code key={key++}>{token.slice(1, -1)}</code>);
    }
    lastIdx = start + token.length;
  }
  if (lastIdx < text.length) parts.push(text.slice(lastIdx));
  return parts;
}

const TypingDots = () => {
  const theme = useTheme();
  return (
    <Stack direction="row" spacing={0.75} alignItems="center" sx={{ py: 1.25, px: 0.5 }}>
      {[0, 1, 2].map(i => (
        <Box key={i} sx={{
          width: 8, height: 8, borderRadius: '50%',
          bgcolor: theme.palette.primary.main,
          animation: `${pulse} 1.4s ease-in-out infinite`,
          animationDelay: `${i * 0.2}s`,
        }} />
      ))}
    </Stack>
  );
};

const inlineCodeSx = {
  '& code': {
    fontFamily: MONO,
    fontSize: '0.875em',
    px: 0.75,
    py: 0.25,
    borderRadius: 0.75,
    bgcolor: 'rgba(255,255,255,0.08)',
    color: 'text.primary',
  },
};

type CellAlign = 'left' | 'center' | 'right';

function parseTableCells(line: string): string[] {
  const t = line.trim();
  const inner = t.startsWith('|') && t.endsWith('|') ? t.slice(1, -1) : t;
  return inner.split('|').map(c => c.trim());
}

function isTableRow(line: string): boolean {
  const t = line.trim();
  return t.startsWith('|') && t.endsWith('|') && t.length >= 3;
}

function isSeparatorRow(line: string): boolean {
  if (!isTableRow(line)) return false;
  const cells = parseTableCells(line);
  return cells.length > 0 && cells.every(c => /^:?-{1,}:?$/.test(c));
}

function parseAlignments(separatorLine: string): CellAlign[] {
  return parseTableCells(separatorLine).map(c => {
    const left = c.startsWith(':');
    const right = c.endsWith(':');
    if (left && right) return 'center';
    if (right) return 'right';
    return 'left';
  });
}

const MarkdownTable = ({
  headers,
  rows,
  aligns,
}: {
  headers: string[];
  rows: string[][];
  aligns: CellAlign[];
}) => {
  const theme = useTheme();
  const align = (i: number): CellAlign => aligns[i] ?? 'left';
  const cellPadding = { px: 1.25, py: 0.75 };

  return (
    <Box
      sx={{
        my: 1.5,
        borderRadius: 1.5,
        overflow: 'auto',
        border: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
        bgcolor: alpha(theme.palette.background.paper, 0.35),
        animation: `${fadeUp} 280ms ease-out`,
      }}
    >
      <Box
        component="table"
        sx={{
          width: '100%',
          borderCollapse: 'collapse',
          fontSize: '0.875rem',
          tableLayout: 'auto',
        }}
      >
        <Box component="thead">
          <Box
            component="tr"
            sx={{ bgcolor: alpha(theme.palette.primary.main, 0.1) }}
          >
            {headers.map((h, i) => (
              <Box
                key={i}
                component="th"
                sx={{
                  ...cellPadding,
                  textAlign: align(i),
                  fontWeight: 700,
                  fontSize: '0.78rem',
                  color: theme.palette.text.primary,
                  textTransform: 'uppercase',
                  letterSpacing: 0.6,
                  borderBottom: `1px solid ${alpha(theme.palette.divider, 0.6)}`,
                  verticalAlign: 'middle',
                  ...inlineCodeSx,
                }}
              >
                {renderInline(h)}
              </Box>
            ))}
          </Box>
        </Box>
        <Box component="tbody">
          {rows.map((row, ri) => (
            <Box
              key={ri}
              component="tr"
              sx={{
                '&:nth-of-type(even)': {
                  bgcolor: alpha(theme.palette.background.default, 0.4),
                },
              }}
            >
              {row.map((cell, ci) => (
                <Box
                  key={ci}
                  component="td"
                  sx={{
                    ...cellPadding,
                    textAlign: align(ci),
                    color: theme.palette.text.primary,
                    borderTop: `1px solid ${alpha(theme.palette.divider, 0.3)}`,
                    verticalAlign: 'top',
                    lineHeight: 1.55,
                    '& strong': { color: theme.palette.text.primary, fontWeight: 700 },
                    '& em': { color: theme.palette.text.secondary, fontStyle: 'italic' },
                    ...inlineCodeSx,
                  }}
                >
                  {renderInline(cell)}
                </Box>
              ))}
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
};

const AiMarkdownRenderer = ({ text }: { text: string }) => {
  const theme = useTheme();
  const lines = text.split('\n');
  const elements: JSX.Element[] = [];

  const bodyTextSx = {
    fontSize: '0.9375rem',
    lineHeight: 1.7,
    color: theme.palette.text.primary,
    '& strong': { color: theme.palette.text.primary, fontWeight: 700 },
    '& em': { color: theme.palette.text.secondary, fontStyle: 'italic' },
    ...inlineCodeSx,
  };

  let i = 0;
  while (i < lines.length) {
    const line = lines[i];

    if (
      isTableRow(line) &&
      i + 1 < lines.length &&
      isSeparatorRow(lines[i + 1])
    ) {
      const headers = parseTableCells(line);
      const aligns = parseAlignments(lines[i + 1]);
      const bodyRows: string[][] = [];
      let j = i + 2;
      while (j < lines.length && isTableRow(lines[j]) && !isSeparatorRow(lines[j])) {
        bodyRows.push(parseTableCells(lines[j]));
        j++;
      }
      elements.push(
        <MarkdownTable
          key={`tbl-${i}`}
          headers={headers}
          rows={bodyRows}
          aligns={aligns}
        />,
      );
      i = j;
      continue;
    }

    if (line.startsWith('## ')) {
      elements.push(
        <Typography key={i} sx={{
          fontSize: '1.0625rem', fontWeight: 700,
          mt: 2, mb: 0.75,
          color: theme.palette.text.primary,
          letterSpacing: -0.1,
          display: 'flex', alignItems: 'center', gap: 1,
          '&::before': {
            content: '""',
            width: 3, height: 14,
            bgcolor: theme.palette.primary.main,
            borderRadius: 0.5,
            flexShrink: 0,
          },
        }}>{line.slice(3)}</Typography>
      );
    } else if (line.startsWith('### ')) {
      elements.push(
        <Typography key={i} sx={{
          fontSize: '0.75rem', fontWeight: 700,
          mt: 1.5, mb: 0.5,
          color: theme.palette.text.secondary,
          textTransform: 'uppercase',
          letterSpacing: 0.8,
        }}>{line.slice(4)}</Typography>
      );
    } else if (line.startsWith('- ') || line.startsWith('* ')) {
      const content = line.slice(2);
      elements.push(
        <Box key={i} sx={{ display: 'flex', gap: 1, mb: 0.375, pl: 0.5 }}>
          <Box sx={{
            width: 4, height: 4, borderRadius: '50%',
            bgcolor: theme.palette.primary.main,
            flexShrink: 0,
            mt: '0.625rem',
          }} />
          <Typography sx={bodyTextSx}>{renderInline(content)}</Typography>
        </Box>
      );
    } else if (line.trim() === '') {
      elements.push(<Box key={i} sx={{ height: 6 }} />);
    } else {
      elements.push(
        <Typography key={i} sx={{ ...bodyTextSx, mb: 0.5 }}>
          {renderInline(line)}
        </Typography>
      );
    }
    i++;
  }

  return <>{elements}</>;
};

const scoreColor = (category: string | undefined, theme: Theme): string => {
  if (!category) return theme.palette.text.primary;
  const c = category.toLowerCase();
  if (c.includes('excell') || c.includes('except')) return theme.palette.success.dark;
  if (c.includes('good') || c.includes('prime')) return theme.palette.success.main;
  if (c.includes('fair') || c.includes('near')) return theme.palette.warning.main;
  if (c.includes('poor') || c.includes('deep') || c.includes('bad') || c.includes('sub')) return theme.palette.error.main;
  return theme.palette.info.main;
};

const riskColor = (level: string | undefined, theme: Theme): string => {
  if (!level) return theme.palette.text.secondary;
  const l = level.toLowerCase();
  if (l === 'low') return theme.palette.success.main;
  if (l === 'medium') return theme.palette.warning.main;
  if (l === 'high') return theme.palette.error.light;
  if (l === 'very_high' || l === 'very-high') return theme.palette.error.main;
  return theme.palette.info.main;
};

const verdictColor = (verdict: string | undefined, theme: Theme): string => {
  if (!verdict) return theme.palette.text.primary;
  const v = verdict.toUpperCase().trim();
  if (v === 'APPROVE') return theme.palette.success.main;
  if (v === 'CONDITIONAL') return theme.palette.warning.main;
  if (v === 'REFER') return theme.palette.info.main;
  if (v === 'DECLINE') return theme.palette.error.main;
  return theme.palette.text.primary;
};

const recommendationColor = (text: string | undefined, theme: Theme): string => {
  if (!text) return theme.palette.text.primary;
  const t = text.toUpperCase().trim();
  if (t.startsWith('APPROVE')) {
    if (t.includes('CONDITIONAL')) return theme.palette.warning.main;
    return theme.palette.success.main;
  }
  if (t.startsWith('CONDITIONAL')) return theme.palette.warning.main;
  if (t.startsWith('REFER')) return theme.palette.info.main;
  if (t.startsWith('DECLINE')) return theme.palette.error.main;
  return theme.palette.text.primary;
};

const extractVerb = (recommendation: string | undefined): string => {
  if (!recommendation) return '';
  return recommendation.split(/\n/)[0]?.trim() ?? '';
};

const formatRiskLabel = (level: string | undefined): string => {
  if (!level) return '';
  return level.replace(/[_-]+/g, ' ').toUpperCase() + ' RISK';
};

interface Widget { type: string; data: Record<string, any> }

const VerdictBanner = ({ scoreCard, riskSummary }: { scoreCard?: Widget; riskSummary?: Widget }) => {
  const theme = useTheme();
  const score = scoreCard?.data?.score;
  const category = scoreCard?.data?.category;
  const modelName = scoreCard?.data?.modelName;
  const scoreClr = scoreColor(category, theme);

  const riskLevel = riskSummary?.data?.riskLevel;
  const recommendation: string = riskSummary?.data?.recommendation ?? '';
  const verdict: string | undefined = riskSummary?.data?.verdict;
  const tier: number | null | undefined = riskSummary?.data?.tier;
  const riskClr = riskColor(riskLevel, theme);
  const recClr = verdict ? verdictColor(verdict, theme) : recommendationColor(recommendation, theme);
  const recVerb = verdict
    ? `${verdict.toUpperCase()}${tier ? ` — Tier ${tier}` : ''}`
    : extractVerb(recommendation);

  const hasLeft = score !== undefined && score !== null;
  const hasRight = !!riskLevel || !!recVerb;
  if (!hasLeft && !hasRight) return null;

  const accentClr = recVerb ? recClr : (riskLevel ? riskClr : scoreClr);

  return (
    <Box sx={{
      mb: 2,
      borderRadius: 2,
      overflow: 'hidden',
      bgcolor: alpha(accentClr, 0.08),
      border: `1px solid ${alpha(accentClr, 0.3)}`,
      borderLeft: `3px solid ${accentClr}`,
      animation: `${fadeUp} 260ms ease-out`,
    }}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        divider={
          hasLeft && hasRight ? (
            <Box sx={{
              bgcolor: alpha(theme.palette.divider, 0.5),
              width: { xs: '100%', sm: '1px' },
              height: { xs: '1px', sm: 'auto' },
            }} />
          ) : undefined
        }
      >
        {hasLeft && (
          <Stack
            direction="row"
            spacing={1.75}
            alignItems="baseline"
            sx={{ px: 2.25, py: 1.75, flexShrink: 0 }}
          >
            <Typography sx={{
              fontFamily: MONO,
              fontSize: '2.375rem',
              fontWeight: 800,
              lineHeight: 1,
              color: scoreClr,
              letterSpacing: -1.25,
            }}>
              {score}
            </Typography>
            <Stack spacing={0.25}>
              {category && (
                <Typography sx={{
                  fontSize: '0.9375rem',
                  fontWeight: 700,
                  color: scoreClr,
                  textTransform: 'capitalize',
                  lineHeight: 1.2,
                }}>
                  {category}
                </Typography>
              )}
              {modelName && (
                <Typography sx={{
                  fontSize: '0.78rem',
                  fontWeight: 600,
                  color: theme.palette.text.secondary,
                  textTransform: 'uppercase',
                  letterSpacing: 0.9,
                }}>
                  {modelName}
                </Typography>
              )}
            </Stack>
          </Stack>
        )}

        {hasRight && (
          <Stack spacing={0.75} sx={{ flex: 1, px: 2.25, py: 1.75, minWidth: 0 }}>
            {riskLevel && (
              <Chip
                label={formatRiskLabel(riskLevel)}
                size="small"
                sx={{
                  alignSelf: 'flex-start',
                  height: 22,
                  fontSize: '0.78rem',
                  fontWeight: 800,
                  letterSpacing: 1,
                  bgcolor: alpha(riskClr, 0.18),
                  color: riskClr,
                  border: `1px solid ${alpha(riskClr, 0.4)}`,
                  '& .MuiChip-label': { px: 1 },
                }}
              />
            )}
            {recVerb && (
              <Typography sx={{
                fontSize: '1.0625rem',
                fontWeight: 700,
                color: recClr,
                lineHeight: 1.35,
                letterSpacing: 0.2,
                whiteSpace: 'normal',
                overflowWrap: 'anywhere',
                wordBreak: 'break-word',
              }}>
                {recVerb}
              </Typography>
            )}
          </Stack>
        )}
      </Stack>
    </Box>
  );
};

const SectionLabel = ({ text, count }: { text: string; count?: number }) => {
  const theme = useTheme();
  return (
    <Stack direction="row" alignItems="center" spacing={0.875} sx={{ mb: 1.25 }}>
      <Typography sx={{
        fontSize: '0.82rem',
        fontWeight: 800,
        letterSpacing: 1.2,
        textTransform: 'uppercase',
        color: alpha(theme.palette.text.primary, 0.9),
      }}>
        {text}
      </Typography>
      {count !== undefined && (
        <Box sx={{
          height: 18, minWidth: 20,
          px: 0.75, borderRadius: 0.75,
          bgcolor: alpha(theme.palette.text.primary, 0.12),
          color: theme.palette.text.primary,
          fontFamily: MONO,
          fontSize: '0.72rem',
          fontWeight: 700,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          {count}
        </Box>
      )}
      <Box sx={{ flex: 1, height: 1, bgcolor: alpha(theme.palette.divider, 0.5) }} />
    </Stack>
  );
};

type Segment =
  | { kind: 'text'; id: string; text: string }
  | { kind: 'widget'; id: string; widget: Widget };

interface Props {
  text: string;
  widgets: Widget[];
  segments?: Segment[];
  isStreaming?: boolean;
  error?: string;
  messageId?: string;
  sessionId?: string;
}

const buildSegmentsFromLegacy = (text: string, widgets: Widget[]): Segment[] => {
  const out: Segment[] = [];
  if (text.length > 0) out.push({ kind: 'text', id: 'legacy-text', text });
  widgets.forEach((w, i) => out.push({ kind: 'widget', id: `legacy-w-${i}`, widget: w }));
  return out;
};

const AnalysisCard = ({ text, widgets, segments: segmentsProp, isStreaming, error, messageId, sessionId }: Props) => {
  const theme = useTheme();
  const segments = segmentsProp && segmentsProp.length > 0
    ? segmentsProp
    : buildSegmentsFromLegacy(text, widgets);
  const hasAnything = segments.length > 0;

  const [isDownloading, setIsDownloading] = useState(false);
  const canDownload = !!messageId && !!sessionId && !isStreaming && !error;

  const handleDownload = async () => {
    if (!messageId || !sessionId || isDownloading) return;
    setIsDownloading(true);
    try {
      await aiAnalysisPdfApi.download({ sessionId, messageId });
    } catch (err) {
      console.error('AI analysis PDF download failed:', err);
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <Box sx={{ mb: 2 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
        {isStreaming ? (
          <Stack direction="row" spacing={0.625} alignItems="center">
            <Box sx={{
              width: 5, height: 5, borderRadius: '50%',
              bgcolor: theme.palette.primary.main,
              animation: `${pulse} 1.2s ease-in-out infinite`,
            }} />
            <Typography sx={{
              fontSize: '0.78rem',
              fontWeight: 700,
              color: theme.palette.primary.main,
              textTransform: 'uppercase',
              letterSpacing: 1.1,
            }}>
              Live
            </Typography>
          </Stack>
        ) : <Box />}
        <VisibilityBadge mode="shared_with_ai" />
      </Stack>

      <Box sx={{
        pl: 1.75,
        borderLeft: `2px solid ${alpha(error ? theme.palette.error.main : theme.palette.primary.main, 0.25)}`,
        ml: 0.5,
      }}>
        {segments.map((seg, idx) => {
          const isLastTextSegment = seg.kind === 'text'
            && !segments.slice(idx + 1).some(s => s.kind === 'text');
          if (seg.kind === 'text') {
            if (seg.text.trim().length === 0) return null;
            return (
              <Box
                key={seg.id}
                sx={{
                  animation: `${fadeUp} 300ms ease-out`,
                  mb: idx < segments.length - 1 ? 1.5 : 0,
                }}
              >
                <AiMarkdownRenderer text={seg.text} />
                {isStreaming && isLastTextSegment && (
                  <Box sx={{
                    display: 'inline-block',
                    width: 8, height: 16,
                    bgcolor: theme.palette.primary.main,
                    ml: 0.25, mb: '-3px',
                    borderRadius: 0.5,
                    animation: `${cursorBlink} 1s steps(1) infinite`,
                  }} />
                )}
              </Box>
            );
          }
          return (
            <Box
              key={seg.id}
              sx={{
                animation: `${fadeUp} 320ms ease-out`,
                mb: idx < segments.length - 1 ? 2 : 0,
              }}
            >
              <AiChatWidgetCard widget={seg.widget} />
            </Box>
          );
        })}

        {isStreaming && !hasAnything && <TypingDots />}

        {canDownload && (
          <Box
            sx={{
              mt: 2,
              pt: 1.5,
              borderTop: `1px solid ${alpha(theme.palette.divider, 0.4)}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1.5,
              animation: `${fadeUp} 320ms ease-out`,
            }}
          >
            <Typography
              sx={{
                fontSize: '0.8125rem',
                color: theme.palette.text.secondary,
                lineHeight: 1.4,
              }}
            >
              Save this report to share or attach to the deal file.
            </Typography>
            <Button
              size="small"
              variant="outlined"
              onClick={handleDownload}
              disabled={isDownloading}
              startIcon={
                isDownloading ? (
                  <CircularProgress size={14} thickness={5} sx={{ color: theme.palette.primary.main }} />
                ) : (
                  <DownloadOutlinedIcon sx={{ fontSize: 18 }} />
                )
              }
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                whiteSpace: 'nowrap',
                borderColor: alpha(theme.palette.primary.main, 0.4),
                color: theme.palette.primary.main,
                '&:hover': {
                  borderColor: theme.palette.primary.main,
                  bgcolor: alpha(theme.palette.primary.main, 0.06),
                },
              }}
            >
              {isDownloading ? 'Preparing PDF…' : 'Download as PDF'}
            </Button>
          </Box>
        )}

        {error && (
          <Box
            sx={{
              mt: hasAnything ? 1.5 : 0,
              px: 1.75, py: 1.25, borderRadius: 1.5,
              bgcolor: alpha(theme.palette.error.main, 0.08),
              border: `1px solid ${alpha(theme.palette.error.main, 0.2)}`,
              animation: `${fadeUp} 280ms ease-out`,
            }}
          >
            <Typography sx={{
              fontSize: '0.875rem',
              fontWeight: 600,
              color: theme.palette.error.main,
              lineHeight: 1.5,
              textTransform: 'uppercase',
              letterSpacing: 0.6,
              mb: 0.5,
            }}>
              {hasAnything ? 'Stream interrupted' : 'Error'}
            </Typography>
            <Typography sx={{
              fontSize: '0.9375rem',
              color: theme.palette.text.primary,
              lineHeight: 1.55,
            }}>
              {error}
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default AnalysisCard;
